import { PropsWithChildren, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import CategoryIcon from '@material-ui/icons/Category';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import ChatIcon from '@material-ui/icons/Chat';
import BuildIcon from '@material-ui/icons/Build';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { useApi, discoveryApiRef, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';

function useFeatureFlags() {
  const config = useApi(configApiRef);
  const get = (key: string) => {
    try {
      return config.getOptionalBoolean(`agentsKb.features.${key}`) ?? false;
    } catch {
      return false;
    }
  };
  return {
    catalog: get('catalog'),
    docs: get('docs'),
    apiDocs: get('apiDocs'),
    scaffolder: get('scaffolder'),
    myGroups: get('myGroups'),
    notifications: get('notifications'),
  };
}

interface Agent {
  id: string;
  slug: string;
  name: string;
  deleted_at: string | null;
}

function DynamicAgentSidebarItems() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [agents, setAgents] = useState<Agent[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const base = await discoveryApi.getBaseUrl('aws-genai');
        const res = await fetchApi.fetch(`${base}/agents`);
        if (!res.ok || cancelled) return;
        const data: Agent[] = await res.json();
        if (!cancelled) setAgents(data.filter(a => !a.deleted_at));
      } catch {
        // best-effort; sidebar degrades gracefully on API failure
      }
    }
    load();
    return () => { cancelled = true; };
  }, [discoveryApi, fetchApi]);

  return (
    <>
      {agents.map(agent => (
        <SidebarItem
          key={agent.id}
          icon={ChatIcon}
          to={`assistant/${agent.slug}`}
          text={agent.name}
        />
      ))}
    </>
  );
}

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {isOpen ? <LogoFull /> : <LogoIcon />}
          {isOpen && (
            <span
              style={{
                fontSize: '10px',
                fontWeight: 500,
                color: 'rgba(255,255,255,0.65)',
                lineHeight: '1.2',
                whiteSpace: 'nowrap',
                letterSpacing: '0.04em',
              }}
            >
              Knowledge Base &amp; Agents
            </span>
          )}
        </div>
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const features = useFeatureFlags();
  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Always visible */}
          <SidebarItem icon={HomeIcon} to="/" text="Home" />
          {/* Feature-flagged items — enable via agentsKb.features.* in app-config.yaml */}
          {features.catalog && <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />}
          {features.myGroups && (
            <MyGroupsSidebarItem
              singularTitle="My Group"
              pluralTitle="My Groups"
              icon={GroupIcon}
            />
          )}
          {features.apiDocs && <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />}
          {features.docs && <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />}
          {features.scaffolder && <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />}
          <SidebarDivider />
          {/* Always visible: agent chat items + builder */}
          <DynamicAgentSidebarItems />
          <SidebarItem
            icon={BuildIcon}
            to="genai-builder"
            text="Agent Builder"
          />
          <SidebarDivider />
          <SidebarScrollWrapper>
            {/* Items in this group will be scrollable if they run out of space */}
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        {features.notifications && <NotificationsSidebarItem />}
        {features.notifications && <SidebarDivider />}
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
